// Composer la séance (D-043, maquette « Coach · composer la séance ») : une page à
// part, pour le staff. Le thème, les présentes et les groupes, la frise de la séance
// (couleur par catégorie, schéma de l'exercice ou du play, heure de chaque bloc), les
// ajouts (exercice, play, modèle, plan précédent), puis « Envoyer aux joueuses » et
// « Lancer la séance ». Les joueuses voient « Au programme » sur la fiche de séance.
import {
  eventById, exerciseDiagrams, occurrenceRoster, responsesFor, saveSessionPlan, sessionPlan, teamPlays,
  type Play, type SessionPlan,
} from '../lib/api';
import { occurrences, parseLocalDate } from '../domain/schedule';
import { expectedOf, resolveAttendance } from '../domain/attendance';
import { attCtx, leadsFor, loadAvail } from './availability-data';
import { CATEGORY_LABEL, move, swapAlt, timeline, totalMinutes, type PlanItem } from '../domain/session';
import { stepSvg } from '../domain/court';
import { displayName, agree } from '../domain/labels';
import { teamOf, type Ctx } from './context';
import { time } from './format';
import { $, $$, html, mount, openSheet, raw, toast, type Html } from './dom';
import { navReplace } from './nav';
import { openLive } from './live';
import { openAddSheet, openItemSheet, openReuseSheet, openSaveTemplateSheet, openTemplatesSheet } from './plan';
import { CAT_COLOR } from './basket';
import { readGroups } from '../domain/groups';
import { groupsLine, openGroupsSheet } from './exo-groups';
import { SIDE_LABEL } from './playbook';
import { drawSeanceExtras, loadSeanceExtras } from './rt-coach';
import { planDrills } from './drills';
import { launchDrill } from './drill-run';
import { KIND_ICON } from '../domain/drills';
import { ARCHIVED_LABEL } from '../domain/library';
import type { Drill } from '../lib/drills-api';
import { renderRtSession } from './rt-teams';

const uid = () => crypto.randomUUID().slice(0, 8);

/** La séance et son plan, une seule fiche à deux segments (D-139, maquette n3c) : « La séance » (présences, infos, la fiche
 *  #/e/…) et « Le plan » (#/seance/…) ; passer de l'un à l'autre remplace l'adresse, le retour reste celui d'origine. */
export const seanceSeg = (eventId: string, date: string, on: 'seance' | 'plan'): Html => html`
  <nav class="seg" aria-label="La séance" data-guide="sc-seg"><a href="#/e/${eventId}/${date}" ${on === 'seance' ? raw('aria-current="page"') : ''}>La séance</a><a href="#/seance/${eventId}/${date}" ${on === 'plan' ? raw('aria-current="page"') : ''} data-guide="sc-seg-plan">Le plan</a></nav>`;

/** `open` : « tirage » ouvre les groupes du premier exercice sur « Tirage au sort » (#/seance/…/tirage : visites guidées). */
export async function renderSeance(root: HTMLElement, ctx: Ctx, eventId: string, date: string, open?: string): Promise<void> {
  const ev = await eventById(eventId);
  const occ = ev ? occurrences(ev, date, date)[0] : undefined;
  const back = `#/e/${eventId}/${date}`;
  if (!ev || !occ) { mount(root, html`<section class="screen"><a class="back" href="#/planning">‹ Planning</a><div class="empty"><p class="empty-title">Cette séance n'existe pas (ou plus).</p></div></section>`); return; }
  const team = teamOf(ctx, ev.team_id);
  // Le responsable technique sans casquette coach de l'équipe la lit et la commente (D-093) : le plan reste au coach.
  if (ctx.full.techOf.has(ev.team_id) && !ctx.full.pureCoach.has(ev.team_id) && !ctx.full.adminOf.some(c => c.id === team?.club_id)) return renderRtSession(root, ctx, eventId, date);
  if (!ctx.staffOf.has(ev.team_id)) { navReplace(back); return; }
  const [roster, responses, avail, leads, stored, plays] = await Promise.all([
    occurrenceRoster(eventId, date), responsesFor(eventId, date), loadAvail(), leadsFor(team?.club_id ?? ctx.club?.id), sessionPlan(eventId, date),
    teamPlays(ev.team_id).catch(() => [] as Play[]),
  ]);
  // Ce que propose le club (D-093) : objectif du cycle, thème du mois, commentaire du responsable.
  const extras = await loadSeanceExtras(ctx, ev, date).catch(() => null);
  let plan: SessionPlan = stored ?? { event_id: eventId, occurrence_date: date, items: [], coach_notes: null, published: false, theme: null, groups: 1 };
  const att = resolveAttendance(roster.filter(r => !r.invited).map(r => r.person_id), roster.filter(r => r.invited).map(r => r.person_id),
    responses, attCtx(avail, ev, occ, null, leads));
  const nameOf = new Map(roster.map(r => [r.person_id, displayName(r)]));
  // Les groupes d'exercices comptent aussi les incertaines (présentes par défaut, D-116).
  const present = expectedOf(att).map(id => ({ id, name: nameOf.get(id) ?? '—' }));
  // Le tirage au sort (D-176) : tout le monde convoqué, les présentes d'abord ; les absentes barrées (pas tirées).
  const expected = new Set(expectedOf(att));
  const drawPeople = [...roster].sort((x, y) => Number(!expected.has(x.person_id)) - Number(!expected.has(y.person_id)))
    .map(r => ({ id: r.person_id, name: nameOf.get(r.person_id) ?? '—', jersey: r.jersey_number }));
  const slotMin = occ.end ? Math.round((occ.end.getTime() - occ.start.getTime()) / 60000) : null;
  const playOf = new Map(plays.map(p => [p.id, p]));
  const diagrams = new Map((await exerciseDiagrams([...new Set(plan.items.map(i => i.exercise_id).filter((x): x is string => !!x))]).catch(() => []))
    .map(d => [d.id, d.diagram]));
  const weekday = parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'long' });

  const thumbOf = (it: PlanItem): Html => {
    const svg = it.play_id ? stepSvg(playOf.get(it.play_id)?.animations[0] as never, 0, 'slice') : it.exercise_id ? stepSvg(diagrams.get(it.exercise_id) as never, 0, 'slice') : null;
    return svg ? raw(svg) : html`<span class="sc-ico" style="background:${CAT_COLOR[it.category]}"></span>`;
  };

  // Le drill de chaque exercice (D-098) : le sien, sinon celui de l'exercice de la bibliothèque.
  let drillOf = new Map<string, Drill>();
  const refreshDrills = () => void planDrills(plan.items).then(m => { drillOf = m; draw(); }).catch(() => undefined);
  const save = async (patch: Partial<SessionPlan>, msg?: string) => {
    plan = { ...plan, ...patch };
    try { await saveSessionPlan(plan); if (msg) toast(msg); } catch { toast('Enregistrement impossible.', 'ko'); }
    draw();
    if (patch.items) refreshDrills();
  };

  const openGroups = (i: number, start?: 'tirage') => {
    const it = plan.items[i];
    if (!it) return;
    const previous = [...plan.items.slice(0, i)].reverse().map(x => readGroups(x.groups)).find(g => g.length) ?? null;
    openGroupsSheet(it.title, readGroups(it.groups), present, previous, (groups, how) =>
      void save({ items: plan.items.map((x, k) => (k === i ? { ...x, groups: groups.length ? groups : null } : x)) },
        how === 'tirage' ? `Tirage verrouillé : ${groups.length} groupes pour « ${it.title} »`
          : groups.length ? `${groups.length} ${groups.length > 1 ? 'groupes' : 'groupe'} pour « ${it.title} »` : 'Tout le monde ensemble'),
    team ? { team: { id: team.id, name: team.name, gender: team.gender, category: team.category }, people: drawPeople, start,
      tvSub: `${team.name} · ${it.title} · séance du ${weekday}` } : undefined);
  };

  const draw = () => {
    const total = totalMinutes(plan.items);
    const t = timeline(plan.items, occ.start);
    const firstExo = plan.items.findIndex(x => !x.play_id);
    mount(root, html`
      <section class="screen sc">
        <div class="pp-nav"><a class="back" href="#/planning">‹ Planning</a>
          <span class="chip ${plan.published ? 'chip-ok' : 'chip-warn'}">${plan.published ? 'Envoyée à l’équipe' : 'Brouillon'}</span></div>
        ${seanceSeg(eventId, date, 'plan')}
        <header class="sc-head">
          <div><p class="eyebrow eyebrow-accent">${time(occ.start)}${occ.end ? ` – ${time(occ.end)}` : ''}${ev.location ? ` · ${ev.location}` : ''}</p>
            <h2>Séance du ${weekday}</h2><p class="muted small">${team?.name ?? ''}</p></div>
          <div class="sc-total"><b>${total}</b>${slotMin ? html`<small> / ${slotMin}</small>` : ''}<span>minutes</span></div>
        </header>
        <section class="block sc-theme">
          <label class="field"><span>Thème de la séance</span>
            <input id="sc-theme" maxlength="200" value="${plan.theme ?? ''}" placeholder="ex. Courir après chaque récupération, repli en 5 secondes"></label>
          <div class="occ-chips">
            <span class="chip chip-ok">${att.present.length} ${agree(team?.gender ?? 'X', 'présent', 'présente', att.present.length)}</span>
            ${att.uncertain.length ? html`<span class="chip chip-warn">${att.uncertain.length} ${agree(team?.gender ?? 'X', 'incertain', 'incertaine', att.uncertain.length)}</span>` : ''}
            ${att.absent.length ? html`<span class="chip chip-ko">${att.absent.length} ${agree(team?.gender ?? 'X', 'absent', 'absente', att.absent.length)}</span>` : ''}
          </div>
        </section>
        ${plan.items.length ? html`<ol class="sc-line">${t.map((s, i) => html`
          <li>
            <span class="sc-at">${time(s.start)}</span>
            <div class="sc-item" style="--cat:${CAT_COLOR[s.item.category]};min-height:${Math.max(56, Math.min(130, (s.item.minutes || 5) * 3.6))}px">
              <button type="button" class="sc-main" data-edit="${i}">
                <span class="sc-thumb">${thumbOf(s.item)}</span>
                <span class="sc-txt"><strong>${s.item.title}</strong>
                  <small>${CATEGORY_LABEL[s.item.category] ?? ''}${s.item.play_id ? ` · play ${SIDE_LABEL[playOf.get(s.item.play_id)?.side ?? 'attaque'] ?? ''}` : ''}${s.item.alt ? ` · plan B : ${s.item.alt.title}` : ''}</small>
                  ${groupsLine(readGroups(s.item.groups))}
                  ${drillOf.get(s.item.id) ? html`<span class="dr-scd">${KIND_ICON[drillOf.get(s.item.id)!.kind]} ${drillOf.get(s.item.id)!.title}</span>${drillOf.get(s.item.id)!.archived_at ? html`<small class="sup-gone">Drill ${ARCHIVED_LABEL}</small>` : ''}` : ''}</span>
                <b class="sc-min">${s.item.minutes}'</b>
              </button>
              <span class="sc-act">
                ${drillOf.get(s.item.id) ? html`<button type="button" class="dr-scplay" data-drill="${i}" aria-label="Lancer le drill ${drillOf.get(s.item.id)!.title}">▶</button>` : ''}
                <button type="button" data-grp="${i}" aria-label="Groupes de l'exercice" title="Groupes" ${i === firstExo ? raw('data-guide="sc-grp"') : ''}><span class="ti ti-people" aria-hidden="true"></span></button>
                ${s.item.alt ? html`<button type="button" data-swap="${i}" aria-label="Passer au plan B" title="Passer au plan B">⇄</button>` : ''}
                <button type="button" data-up="${i}" aria-label="Monter" ${i === 0 ? 'disabled' : ''}>▲</button>
                <button type="button" data-down="${i}" aria-label="Descendre" ${i === plan.items.length - 1 ? 'disabled' : ''}>▼</button>
              </span>
            </div>
          </li>`)}
          <li class="sc-end"><span class="sc-at">${time(t[t.length - 1]!.end)}</span><span class="muted small">fin${occ.end && t[t.length - 1]!.end > occ.end ? ' — dépasse le créneau' : ''}</span></li>
        </ol>` : html`<p class="muted">Pas encore de plan. Pars d'une séance type du club, reprends une séance précédente, ou ajoute tes exercices et tes plays.</p>`}
        <div class="sc-add">
          <button type="button" class="btn btn-dashed" id="sc-ex" data-guide="sc-add">+ Exercice</button>
          <button type="button" class="btn btn-dashed" id="sc-play">+ Play</button>
          <button type="button" class="btn btn-dashed" id="sc-tpl">Séances types</button>
        </div>
        <div class="btn-row">
          <button type="button" class="btn btn-link btn-sm" id="sc-reuse">Reprendre une séance précédente</button>
          ${plan.items.length ? html`<button type="button" class="btn btn-link btn-sm" id="sc-save-tpl">Enregistrer comme séance type</button>` : ''}
        </div>
        <label class="field"><span>Notes du coach (privées)</span>
          <textarea id="sc-notes" rows="2" maxlength="4000" placeholder="Points d'attention, qui surveiller…">${plan.coach_notes ?? ''}</textarea></label>
        ${plan.items.length ? html`
          <div class="sc-bar">
            <button type="button" class="btn ${plan.published ? 'btn-ghost' : 'btn-ghost'}" id="sc-send" data-guide="sc-send">${plan.published ? 'Retirer de l’équipe' : 'Envoyer à l’équipe'}</button>
            <button type="button" class="btn btn-primary" id="sc-live">▶ Lancer la séance</button>
          </div>` : ''}
      </section>`);

    if (extras) drawSeanceExtras(root, extras, plan.items, (items, msg) => void save({ items: [...plan.items, ...items] }, msg));
    $<HTMLInputElement>(root, '#sc-theme')!.addEventListener('change', e => void save({ theme: (e.target as HTMLInputElement).value.trim() || null }));
    $<HTMLTextAreaElement>(root, '#sc-notes')!.addEventListener('change', e => void save({ coach_notes: (e.target as HTMLTextAreaElement).value.trim() || null }));
    for (const b of $$<HTMLButtonElement>(root, '[data-grp]')) b.addEventListener('click', () => {
      const i = Number(b.dataset.grp);
      const previous = [...plan.items.slice(0, i)].reverse().map(x => readGroups(x.groups)).find(g => g.length) ?? null;
      openGroups(i);
    });
    $(root, '#sc-ex')!.addEventListener('click', () => void openAddSheet(team, item => void save({ items: [...plan.items, item] })));
    $(root, '#sc-play')!.addEventListener('click', () => openPlayPick(plays, item => void save({ items: [...plan.items, item] })));
    $(root, '#sc-tpl')!.addEventListener('click', () => team && void openTemplatesSheet(team, plan.items.length > 0, (items, mode, notes, theme) =>
      void save({ items: mode === 'append' ? [...plan.items, ...items] : items, coach_notes: plan.coach_notes || notes, theme: plan.theme || theme })));
    $(root, '#sc-reuse')!.addEventListener('click', () => team && void openReuseSheet(team, ev, date, items => void save({ items })));
    $(root, '#sc-save-tpl')?.addEventListener('click', () => team && openSaveTemplateSheet(team, ev, plan));
    $(root, '#sc-send')?.addEventListener('click', () => void save({ published: !plan.published }, plan.published ? 'Retirée : seuls les coachs la voient' : 'Envoyée : l’équipe voit le programme (sans plan B ni notes)'));
    $(root, '#sc-live')?.addEventListener('click', () => openLive(plan.items, ev.title, id => nameOf.get(id) ?? '—', expectedOf(att), team));
    for (const b of $$<HTMLButtonElement>(root, '[data-drill]')) b.addEventListener('click', () => {
      const d = drillOf.get(plan.items[Number(b.dataset.drill)]!.id);
      if (d) void launchDrill(d, { team, staff: true });
    });
    for (const b of $$<HTMLButtonElement>(root, '[data-up]')) b.addEventListener('click', () => { const i = Number(b.dataset.up); void save({ items: move(plan.items, i, i - 1) }); });
    for (const b of $$<HTMLButtonElement>(root, '[data-down]')) b.addEventListener('click', () => { const i = Number(b.dataset.down); void save({ items: move(plan.items, i, i + 1) }); });
    for (const b of $$<HTMLButtonElement>(root, '[data-swap]')) b.addEventListener('click', () => {
      const i = Number(b.dataset.swap);
      void save({ items: plan.items.map((x, k) => (k === i ? swapAlt(x) : x)) }, 'Plan B activé');
    });
    for (const b of $$<HTMLButtonElement>(root, '[data-edit]')) b.addEventListener('click', () => {
      const i = Number(b.dataset.edit);
      const it = plan.items[i]!;
      if (it.play_id && playOf.get(it.play_id)) {
        openSheet(it.title, html`
          <a class="btn btn-ghost" href="#/play/${it.play_id}">Ouvrir le play</a>
          <form class="form" id="f-pi" novalidate>
            <label class="field"><span>Durée (min)</span><input name="minutes" type="number" min="1" max="180" value="${it.minutes}"></label>
            <label class="field"><span>Consignes pour la séance</span><textarea name="description" rows="3" maxlength="4000">${it.description ?? ''}</textarea></label>
            <button class="btn btn-primary" type="submit">Enregistrer</button>
            <button type="button" class="btn btn-danger-ghost" id="b-pi-rm">Retirer de la séance</button>
          </form>`, (sheet, close) => {
          const f = $<HTMLFormElement>(sheet, '#f-pi')!;
          f.addEventListener('submit', e => {
            e.preventDefault();
            const d = new FormData(f);
            close();
            void save({ items: plan.items.map((x, k) => (k === i ? { ...x, minutes: Math.max(1, Number(d.get('minutes')) || 10), description: String(d.get('description') ?? '').trim() || null } : x)) });
          });
          $(sheet, '#b-pi-rm')!.addEventListener('click', () => { close(); void save({ items: plan.items.filter((_, k) => k !== i) }); });
        });
        return;
      }
      void openItemSheet(it, {
        onSave: item => void save({ items: plan.items.map((x, k) => (k === i ? item : x)) }),
        onRemove: () => void save({ items: plan.items.filter((_, k) => k !== i) }),
      }, { team });
    });
  };
  draw();
  refreshDrills();
  if (open === 'tirage') { const i = plan.items.findIndex(x => !x.play_id); if (i >= 0) openGroups(i, 'tirage'); }
}

/** Ajouter un play de l'équipe à la séance (consignes reprises, 10 min par défaut). */
function openPlayPick(plays: Play[], onAdd: (item: PlanItem) => void) {
  openSheet('Ajouter un play', plays.length ? html`
    <input class="search" type="search" placeholder="Chercher un play" aria-label="Chercher un play">
    <ul class="pick-list">${plays.map(p => html`<li><button type="button" data-p="${p.id}" data-q="${`${p.title} ${p.tag ?? ''}`.toLowerCase()}">
      <span class="pick-main"><strong>${p.title}</strong><span class="muted small">${SIDE_LABEL[p.side] ?? ''}${p.tag ? ` · ${p.tag}` : ''}</span></span>
      <span class="chip chip-ok">Ajouter</span></button></li>`)}</ul>` : html`<p class="muted">Pas encore de play pour cette équipe (onglet Basket).</p>`, (sheet, close) => {
    const s = $<HTMLInputElement>(sheet, '.search');
    s?.addEventListener('input', () => { const q = s.value.trim().toLowerCase(); $$(sheet, '[data-p]').forEach(b => { b.parentElement!.hidden = !!q && !b.dataset.q!.includes(q); }); });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-p]')) b.addEventListener('click', () => {
      const p = plays.find(x => x.id === b.dataset.p)!;
      close();
      onAdd({ id: uid(), title: p.title, minutes: 10, category: 'tactique', description: p.description, play_id: p.id });
    });
  });
}
